import React from "react";
import { motion } from "framer-motion";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const isProcessing = (status, fileUrl) =>
  status === "PROCESSING" ||
  status === "requested" ||
  status === "pending" ||
  status === "REQUESTED" ||
  status === "PENDING" ||
  (!fileUrl && status !== "COMPLETED" && status !== "GENERATED");

const isFailed = (status) =>
  status === "FAILED" || status === "failed" || status === "ERROR";

// Returns a bare relative phrase like "2 days ago" / "yesterday" / "just now".
const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

// ── A single story card ──
const StoryCard = ({ story, onClick }) => {
  const title = story.name || story.title || "Untitled Story";
  const thumb = story.thumbnail_url || story.cover_image_url || "/assets/story-placeholder.png";
  const failed = isFailed(story.status);
  const processing = !failed && isProcessing(story.status, story.file_url);
  const percent = Math.max(0, Math.min(100, Number(story.status_percentage) || 0));
  const when = relativeTime(story.created_at || story.updated_at);

  return (
    <motion.button
      type="button"
      onClick={() => !failed && onClick?.(story)}
      whileHover={!failed ? { y: -4 } : undefined}
      whileTap={!failed ? { scale: 0.985 } : undefined}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      className="text-left"
      style={{
        width: "100%",
        background: "#FFFFFF",
        border: "2px solid rgba(11,26,51,0.07)",
        borderRadius: 20,
        overflow: "hidden",
        padding: 0,
        cursor: failed ? "default" : "pointer",
        opacity: failed ? 0.75 : 1,
        boxShadow: "0px 4px 0px rgba(11,26,51,0.05), 0px 12px 26px rgba(11,26,51,0.08)",
        outline: "none",
      }}
      aria-label={`Open ${title}`}
    >
      {/* Thumbnail */}
      <div className="relative" style={{ width: "100%", aspectRatio: "16 / 9", background: "#0B0F1F" }}>
        <img
          src={thumb}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.target.src = "/assets/story-placeholder.png"; }}
          style={{ filter: processing ? "blur(2px) brightness(0.65)" : "none" }}
        />

        {/* Processing % badge */}
        {processing && percent > 0 && (
          <span
            style={{
              ...FREDOKA,
              position: "absolute",
              top: 8,
              left: 8,
              background: "rgba(11,26,51,0.72)",
              color: "#FFFFFF",
              fontSize: 11,
              fontWeight: 800,
              padding: "3px 9px",
              borderRadius: 999,
            }}
          >
            {percent}%
          </span>
        )}

        {/* Failed badge */}
        {failed && (
          <span
            style={{
              ...FREDOKA,
              position: "absolute",
              top: 8,
              left: 8,
              background: "#DC2626",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              padding: "3px 8px",
              borderRadius: 6,
            }}
          >
            Failed
          </span>
        )}

        {/* Processing spinner overlay */}
        {processing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "3px solid rgba(255,255,255,0.3)",
                borderTopColor: "#FFFFFF",
                animation: "fs-spin 1s linear infinite",
              }}
            />
          </div>
        )}

        {/* Play button — on-theme chunky blue */}
        {!processing && !failed && (
          <span
            className="absolute flex items-center justify-center"
            style={{
              right: 12,
              bottom: 12,
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "#0065F8",
              border: "2.5px solid #FFFFFF",
              boxShadow: "0px 4px 0px #073B8E, 0px 8px 18px rgba(0,101,248,0.35)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#FFFFFF" style={{ marginLeft: 2 }}>
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        )}
      </div>

      {/* Text */}
      <div style={{ padding: "12px 14px 14px" }}>
        <p
          style={{
            ...FREDOKA,
            color: "#0B1A33",
            fontWeight: 700,
            fontSize: 15,
            letterSpacing: "-0.01em",
            margin: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </p>
        <p style={{ ...FREDOKA, color: "#8A98AD", fontSize: 12.5, fontWeight: 500, margin: "3px 0 0" }}>
          {failed ? "Tap to retry" : processing ? "Generating…" : when ? `Created ${when}` : ""}
        </p>
      </div>

      {/* Generation progress bar (processing only) */}
      {processing && (
        <div style={{ height: 4, background: "rgba(11,26,51,0.08)" }}>
          <div
            style={{
              height: "100%",
              width: `${Math.max(6, percent)}%`,
              background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
              transition: "width 400ms ease",
            }}
          />
        </div>
      )}
    </motion.button>
  );
};

// ── Empty state ──
const EmptyState = ({ onCreate }) => (
  <div
    className="p-10 text-center"
    style={{
      background: "#FFFFFF",
      border: "2px solid rgba(11,26,51,0.07)",
      borderRadius: 24,
      boxShadow: "0px 4px 0px rgba(11,26,51,0.05), 0px 12px 26px rgba(11,26,51,0.08)",
    }}
  >
    <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 17, margin: "0 0 4px" }}>
      No stories yet
    </p>
    <p style={{ ...FREDOKA, color: "#8A98AD", fontSize: 14, margin: "0 0 16px" }}>
      Create your first story to see it here.
    </p>
    <button
      type="button"
      onClick={onCreate}
      className="px-5 py-2.5 transition-all active:scale-[0.98]"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 14,
        border: "none",
        borderRadius: 999,
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: "pointer",
      }}
    >
      + Create your first story
    </button>
  </div>
);

/**
 * FilmStrip — "Your Stories" cards.
 * - Collapsed: a horizontal scroll row of fixed-width cards.
 * - Expanded: a responsive grid that wraps.
 */
const FilmStrip = ({ stories = [], expanded = false, onStoryClick, onCreate }) => {
  if (stories.length === 0) {
    return (
      <>
        <EmptyState onCreate={onCreate} />
        <style>{filmstripKeyframes}</style>
      </>
    );
  }

  if (expanded) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(258px, 1fr))",
          gap: 18,
        }}
      >
        {stories.map((story) => (
          <StoryCard key={story.id} story={story} onClick={onStoryClick} />
        ))}
        <style>{filmstripKeyframes}</style>
      </div>
    );
  }

  return (
    <>
      <div
        className="fs-scroller"
        style={{
          display: "flex",
          gap: 18,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          paddingBottom: 6,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {stories.map((story) => (
          <div key={story.id} className="fs-card" style={{ scrollSnapAlign: "start" }}>
            <StoryCard story={story} onClick={onStoryClick} />
          </div>
        ))}
      </div>
      <style>{filmstripKeyframes}</style>
    </>
  );
};

const filmstripKeyframes = `
  .fs-scroller::-webkit-scrollbar { display: none; }
  @keyframes fs-spin { to { transform: rotate(360deg); } }
  /* Exactly N whole cards fill the row width (no half-card peeking at the edge). */
  .fs-card { flex: 0 0 calc((100% - 18px) / 2); }
  @media (min-width: 640px) { .fs-card { flex-basis: calc((100% - 36px) / 3); } }
  @media (min-width: 1024px) { .fs-card { flex-basis: calc((100% - 54px) / 4); } }
`;

export default FilmStrip;
